import React from "react";
import { K } from "../theme";

// One renderer for a form definition from /api/form-templates, used by the
// new joiner's joining link (interactive) and by Preview in Email/Form
// Templates (readOnly). Shared on purpose: Preview must show exactly what
// the person filling it sees, so both draw the same markup.

const BOX = "w-full px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none";

const FieldLabel = ({ field }) => (
  <div className="text-[12px] text-t3 mb-1">
    {field.label}{field.required && <span style={{ color: K.r }}> *</span>}
    {field.restricted && <span className="text-t3"> ({field.restricted})</span>}
  </div>
);

const ERR_BOX = { borderColor: K.r, background: `${K.r}0d` };

const Field = ({ field, value, onChange, readOnly, error }) => {
  const errBox = error ? ERR_BOX : undefined;
  const errMsg = error ? <div className="text-[12px] mt-1" style={{ color: K.r }}>{error}</div> : null;
  const val = value ?? (field.type === "checkbox" ? false : "");
  const set = (v) => onChange?.(field.id, v);

  if (field.type === "checkbox") {
    return (
      <label data-field-error={error ? "1" : undefined} style={errBox} className={`sm:col-span-2 flex items-start gap-2 p-2 rounded-lg bg-bg2 border border-b0 ${readOnly ? "" : "cursor-pointer"}`}>
        <input type="checkbox" checked={!!val} disabled={readOnly} onChange={(e) => set(e.target.checked)} className="mt-[3px]" />
        <span className="text-[14px] text-t1">{field.label}{field.required && <span style={{ color: K.r }}> *</span>}</span>
      </label>
    );
  }

  if (field.type === "signature") {
    return (
      <div className="sm:col-span-2" data-field-error={error ? "1" : undefined}>
        <FieldLabel field={field} />
        <input
          type="text" value={val} disabled={readOnly} onChange={(e) => set(e.target.value)}
          placeholder="Type your full name as your signature"
          className={BOX} style={{ fontStyle: "italic", ...errBox }}
        />
        {errMsg}
        <div className="text-[12px] text-t3 mt-1">Typing your name here counts as your signature on this declaration.</div>
      </div>
    );
  }

  // Filled from the employee's record when the form opens for real.
  if (field.type === "read") {
    return (
      <div>
        <FieldLabel field={field} />
        <div className={`${BOX} text-t3`}>Filled in automatically</div>
      </div>
    );
  }

  if (field.type === "file") {
    return (
      <div>
        <FieldLabel field={field} />
        <div className={`${BOX} text-t3`}>File upload</div>
      </div>
    );
  }

  return (
    <div className={field.type === "textarea" ? "sm:col-span-2" : ""} data-field-error={error ? "1" : undefined}>
      <FieldLabel field={field} />
      {field.type === "select" || field.type === "rating" ? (
        <select value={val} disabled={readOnly} onChange={(e) => set(e.target.value)} className={BOX} style={errBox}>
          <option value="">Select</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : field.type === "textarea" ? (
        <textarea value={val} disabled={readOnly} onChange={(e) => set(e.target.value)} className={`${BOX} min-h-[60px] resize-y`} style={errBox} />
      ) : (
        <input
          type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
          value={val} disabled={readOnly} onChange={(e) => set(e.target.value)} className={BOX} style={errBox}
        />
      )}
      {errMsg}
      {field.note && <div className="text-[12px] text-t3 mt-1">{field.note}</div>}
    </div>
  );
};

// values: { [fieldId]: value }. readOnly draws it without letting anyone
// type; showName includes the form's own name and summary.
// errors: { [fieldId]: message } — those fields are drawn in red with the message.
export const FormRenderer = ({ form, values = {}, onChange, readOnly, showName = true, errors }) => {
  if (!form) return null;
  return (
    <div>
      {showName && (
        <>
          <div className="text-[14px] font-bold text-t0">{form.name}</div>
          {form.summary && <div className="text-[12px] text-t3 mt-[2px] mb-2">{form.summary}</div>}
        </>
      )}

      {/* A statutory document's printed heading block. */}
      {form.documentHeading && (
        <div className="p-3 rounded-lg bg-bg2 border border-b0 mb-3">
          <div className="text-[13px] text-t0 font-semibold whitespace-pre-line">{form.documentHeading}</div>
          {form.documentSubheading && <div className="text-[12px] text-t2 mt-2 whitespace-pre-line">{form.documentSubheading}</div>}
        </div>
      )}

      {(form.sections || []).map((sec) => {
        const fields = (sec.fields || []).filter((f) => !f.hidden);
        if (!fields.length && !(sec.documentText || []).length) return null;
        return (
          <div key={sec.id} className="mb-3">
            {sec.title && <div className="text-[12px] font-semibold text-t2 uppercase tracking-wider mb-1">{sec.title}</div>}
            {(sec.note || sec.audience) && <div className="text-[12px] text-t3 mb-2">{sec.note || sec.audience}</div>}
            {(sec.documentText || []).length > 0 && (
              <div className="p-3 rounded-lg bg-bg2 border border-b0 mb-2">
                {sec.documentText.map((line, li) => (
                  <div key={li} className="text-[13px] text-t1 leading-[1.6] mb-1 last:mb-0">{line}</div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {fields.map((f) => (
                <Field key={f.id} field={f} value={values[f.id]} onChange={onChange} readOnly={readOnly} error={errors?.[f.id]} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
